'use client'

// Initialize i18n for client-side
import '@/app/i18n/client'
import { AuthStateProvider } from '@/app/providers/auth'
import { GlobalNotifications } from '@/components/GlobalNotifications'
import appVariables from '@/config'
import { FLAGS_BASE_URL } from '@/constants/flags'
import { initAmplify } from '@/helpers/amplifyConfig'
import { apolloInit } from '@/helpers/apollo/ApolloClient'
import { ApolloProvider } from '@apollo/client'
import { FlagsProvider } from '@caterdesk/flags-react'

import React, { Suspense, useEffect } from 'react'

declare global {
  interface Window {
    __datadogRumInitialized?: boolean
    __datadogLogsInitialized?: boolean
  }
}

// Manual only: nothing is forwarded automatically, so we control what is sent
// (and so what is billed). Use datadogLogs.logger.* to send a log.
const initDatadogLogs = () => {
  if (typeof window === 'undefined') return
  if (window.__datadogLogsInitialized) return

  window.__datadogLogsInitialized = true

  import(/* webpackChunkName: "@datadog/browser-logs" */ '@datadog/browser-logs').then(
    ({ datadogLogs }) => {
      if (!appVariables.DATADOG_CLIENT_TOKEN) return

      datadogLogs.init({
        clientToken: appVariables.DATADOG_CLIENT_TOKEN,
        site: appVariables.DATADOG_SITE,
        service: 'fusion',
        env: appVariables.FEEDR_ENVIRONMENT,
        version: appVariables.COMMIT_SHA,
        forwardErrorsToLogs: false,
        forwardConsoleLogs: [],
        forwardReports: [],
        sessionSampleRate: 100,
        telemetrySampleRate: 0,
      })
    },
  )
}

const initDatadogRum = () => {
  if (typeof window === 'undefined') return
  if (window.__datadogRumInitialized) return

  window.__datadogRumInitialized = true

  import(/* webpackChunkName: "@datadog/browser-rum" */ '@datadog/browser-rum').then(
    ({ datadogRum }) => {
      if (appVariables.DATADOG_APPLICATION_ID && appVariables.DATADOG_CLIENT_TOKEN) {
        datadogRum.init({
          applicationId: appVariables.DATADOG_APPLICATION_ID,
          clientToken: appVariables.DATADOG_CLIENT_TOKEN,
          site: appVariables.DATADOG_SITE,
          service: 'fusion',
          env: appVariables.FEEDR_ENVIRONMENT,
          version: appVariables.COMMIT_SHA,
          sessionReplaySampleRate: 100,
          trackUserInteractions: true,
          enableExperimentalFeatures: ['clickmap'],
          defaultPrivacyLevel: 'mask-user-input',
          allowedTracingUrls: [
            /https:\/\/(.*\.)?feedr\.co/,
            /https:\/\/eatfirstdev.com/,
            /https:\/\/eatfirst.caterdesk.dev/,
            /https:\/\/eatfirst.com/,
          ],
        })

        datadogRum.startSessionReplayRecording()
      }
    },
  )
}

initDatadogRum()
initDatadogLogs()

const Providers: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [apolloClient] = React.useState(() => apolloInit())

  useEffect(() => {
    if (typeof window !== 'undefined') {
      initAmplify()
    }
  }, [])

  const content = (
    <ApolloProvider client={apolloClient}>
      <Suspense>
        <AuthStateProvider>{children}</AuthStateProvider>
      </Suspense>
    </ApolloProvider>
  )

  return (
    <FlagsProvider
      apiKey={appVariables.FLAGS_API_KEY}
      baseUrl={FLAGS_BASE_URL}
      service="fusion"
      debug={appVariables.FEEDR_ENVIRONMENT !== 'prod'}>
      {content}
    </FlagsProvider>
  )
}

export default Providers
